import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import ApiClient from 'c-data/apiClient';
import type {
    ElasticSearchFilters,
    ElasticSearchFrameIdMapDataResponse,
    ElasticSearchSummaryResponse,
    PCADisplayType,
} from 'c-sdk';
import { EsPCAReportField, EsPCAReportFilterField } from 'c-sdk';
import type { AllEsPCAFieldValues } from 'c-main/Lib';
import { allFieldValuesEs } from 'c-main/Lib';
import {
    atom_environmentMode,
    atom_source,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import type { ByType } from './lib';
import { collectLeaves, mergeFilterOptions } from './lib';

export type CombinedTypeData = {
    summary: ElasticSearchSummaryResponse['data'];
    /** Leaves grouped by day of week and hour, for the hour chart. */
    dowHour: Array<Record<string, unknown>>;
    /** Per-frame delivery for the map heatmap; classic and digital frames never collide. */
    frames: ElasticSearchFrameIdMapDataResponse['data'];
};

type State = {
    data: ByType<CombinedTypeData> | null;
    filterValues: AllEsPCAFieldValues | null;
    error: string | null;
    isLoading: boolean;
};

/**
 * Each display type is requested exactly as its own Summary requests it -- `display_type` set,
 * no date range -- so the classic half matches the Classic report. No date range also means the
 * API excludes projected rows and returns settled days only.
 */
const fetchType = async (
    campaignId: number,
    environment: string,
    source: string,
    type: PCADisplayType,
    fieldFilters: ElasticSearchFilters,
) => {
    const filters = { ...fieldFilters, [EsPCAReportFilterField.DisplayType]: [type] };
    const [summary, dowHour, frames] = await Promise.all([
        ApiClient.ReportsElasticSearch.summary({
            campaignId,
            environment,
            source,
            fieldFilters: filters,
            showStats: true,
            showFilters: true,
        }).request,
        ApiClient.ReportsElasticSearch.pcaGroupByField({
            campaignId,
            environment,
            source,
            fields: [EsPCAReportField.Dow, EsPCAReportField.Hour],
            fieldFilters: filters,
            showFilters: false,
        }).request,
        ApiClient.ReportsElasticSearch.frameIdMapData({
            campaignId,
            environment,
            source,
            fieldFilters: filters,
        }).request,
    ]);
    return {
        data: {
            summary: summary?.data?.data,
            dowHour: collectLeaves(dowHour?.data?.data),
            frames: frames?.data?.data ?? {},
        } as CombinedTypeData,
        filters: summary?.data?.filters,
    };
};

const useCombinedReport = (
    campaignId: number,
    types: PCADisplayType[],
    fieldFilters: ElasticSearchFilters,
) => {
    const environment = useAtomValue(atom_environmentMode) || 'live';
    const source = useAtomValue(atom_source);
    const [state, setState] = useState<State>({
        data: null,
        filterValues: null,
        error: null,
        isLoading: true,
    });
    const typesKey = types.join(',');
    const filtersKey = JSON.stringify(fieldFilters);

    useEffect(() => {
        let cancelled = false;
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        (async () => {
            try {
                const results = await Promise.all(
                    types.map(t => fetchType(campaignId, environment, source, t, fieldFilters)),
                );
                if (cancelled) return;
                const data: ByType<CombinedTypeData> = {};
                types.forEach((t, i) => {
                    data[t] = results[i].data;
                });
                setState(prev => ({
                    data,
                    // Options come from the unfiltered response, so they don't narrow as filters
                    // are applied -- the same list the other reports offer.
                    filterValues:
                        prev.filterValues ??
                        allFieldValuesEs(mergeFilterOptions(results.map(r => r.filters))),
                    error: null,
                    isLoading: false,
                }));
            } catch (e) {
                if (!cancelled) {
                    setState(prev => ({
                        ...prev,
                        isLoading: false,
                        error: String(e?.message ?? e ?? 'Error'),
                    }));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // types and fieldFilters are tracked by their keys
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, environment, source, typesKey, filtersKey]);

    return useMemo(() => state, [state]);
};

export default useCombinedReport;
